import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useSubscriptionAccess } from '@/hooks/use-subscription-access';
import { billingApi } from '@/lib/billing-api';
import { getErrorMessage } from '@/lib/errors';
import { listInWords, longDate, trialLimitsInWords } from '@/lib/trial';
import type { Subscription } from '@/types/api';

/**
 * Explains why a customer cannot change anything, and where to fix it.
 *
 * Shown *before* they try, because the alternative is finding out by having a form rejected
 * halfway through filling it in. The 402 from the API remains the authority — this is the
 * courtesy, not the enforcement, and it cannot grant anything the server would refuse.
 *
 * The wording comes from the API rather than from here, so this and the enforcing listener can
 * never disagree about what is wrong.
 *
 * ## Starting the trial from here
 *
 * An account that has never had a subscription is usually on a trial already: one starts when a new
 * account confirms its address. Accounts made before the trial existed, or whose automatic start
 * failed, land here instead, and the quickest way out of "you cannot change anything" is a button
 * that fixes it where the sentence is. It is offered only when the API says this account can start
 * one, and the API refuses a second trial regardless.
 *
 * Staff see nothing: they hold no subscription, and telling an administrator to "choose a plan"
 * would be nonsense.
 */
export function SubscriptionNotice() {
  const { permitsChanges, isStaff, isLoaded, reason, selfServiceable, servicesPaused, trial, pastDueUntil } =
    useSubscriptionAccess();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [started, setStarted] = useState<Subscription | null>(null);

  const start = useMutation({
    mutationFn: billingApi.startTrial,
    onSuccess: (subscription) => {
      setStarted(subscription);
      // Everything a subscription decides changes at once — access, every quota, the Command
      // Runner allowance — so every cached answer is out of date, not only the billing one.
      void queryClient.invalidateQueries();
    },
  });

  // Said once, where the button was pressed, and then out of the way: the notice has nothing else
  // to say to an account that can now change things.
  if (started !== null) {
    return (
      <Alert variant="success">
        Your free trial has started
        {started.current_period_end !== null ? ` and runs until ${longDate(started.current_period_end)}` : ''}. Connect
        a server to begin.
      </Alert>
    );
  }

  // A renewal payment failed and the provider is retrying it. Everything still works, which is
  // exactly why it has to be said: the first sign would otherwise be monitoring stopping a month on.
  // Two short sentences and a link: the date is the one fact the customer needs.
  if (!isStaff && isLoaded && permitsChanges && pastDueUntil !== null) {
    return (
      <Alert variant="warning">
        Renewal payment failed. Your plan stays active until {longDate(pastDueUntil)}.{' '}
        {pathname.startsWith('/billing') ? (
          'Update your payment method below.'
        ) : (
          <Link to="/billing" className="font-medium underline underline-offset-2">
            Update payment method
          </Link>
        )}
      </Alert>
    );
  }

  if (isStaff || !isLoaded || permitsChanges || reason === null) return null;

  // The reason already ends with what to do about it, so all this adds is the consequence and,
  // where it helps, somewhere to go. Pointing at billing is no use to somebody already reading
  // the billing page, and none at all for a suspension only support can lift.
  const offerBilling = selfServiceable && !pathname.startsWith('/billing');
  const offerTrial = trial?.can_start === true;
  const includes = trial !== null ? trialLimitsInWords(trial.limits) : [];

  return (
    <Alert variant="warning">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p>
            {reason} Meanwhile you can still see everything you already have, but adding and changing
            infrastructure is disabled
            {servicesPaused
              ? ', and monitor checks, scheduled backups, restore tests and metrics collection are paused.'
              : '.'}
            {offerBilling && (
              <>
                {' '}
                <Link to="/billing" className="font-medium underline underline-offset-2">
                  {offerTrial ? 'See plans' : 'Go to billing'}
                </Link>
                .
              </>
            )}
          </p>
          {offerTrial && trial.days !== null && (
            <p className="mt-1 text-xs opacity-90">
              The free trial lasts {trial.days} days
              {includes.length > 0 ? ` and includes ${listInWords(includes)}` : ''}. No card is needed.
            </p>
          )}
          {start.isError && <p className="mt-1 font-medium">{getErrorMessage(start.error)}</p>}
        </div>

        {offerTrial && (
          <Button size="sm" className="shrink-0 self-start sm:self-center" onClick={() => start.mutate()} disabled={start.isPending}>
            {start.isPending && <Loader2 className="animate-spin" />}
            Start free trial
          </Button>
        )}
      </div>
    </Alert>
  );
}
